import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Box, Package, TrendingUp, ArrowUpDown, Archive, Settings2, Building2 } from 'lucide-react';
import { PageHeader } from '@/components/PageHeader';
import { StoreSwitcher } from '@/components/layout/StoreSwitcher';
import { Button } from '@/components/ui/button';
import { useHasRole } from '@/features/auth/AuthProvider';
import { fetchCategories, fetchBrands } from './setupQueries';
import { fetchAllInventory } from './inventoryQueries';
import { InventorySetupDialog } from './InventorySetupDialog';
import { ImportProductsDialog } from './ImportProductsDialog';
import { CategoriesTab } from './tabs/CategoriesTab';
import { BrandsTab } from './tabs/BrandsTab';
import { ProductsTab } from './tabs/ProductsTab';
import { InventoryLedgerTab } from './tabs/InventoryLedgerTab';
import { AdjustOutTab } from './tabs/AdjustOutTab';
import { SuppliersTab } from './tabs/SuppliersTab';
import { ArchivedTab } from './tabs/ArchivedTab';
import { cn } from '@/lib/utils';

type Tab = 'categories' | 'brands' | 'products' | 'inventory' | 'adjustout' | 'suppliers' | 'archived';

const TABS: { key: Tab; label: string; icon: typeof Box }[] = [
  { key: 'products', label: 'All Products', icon: Package },
  { key: 'categories', label: 'Categories', icon: Box },
  { key: 'brands', label: 'Brands', icon: Building2 },
  { key: 'inventory', label: 'Inventory', icon: TrendingUp },
  { key: 'adjustout', label: 'Adjust Out', icon: ArrowUpDown },
  { key: 'suppliers', label: 'Suppliers', icon: Package },
  { key: 'archived', label: 'Archived', icon: Archive },
];

export function DatahubPage() {
  const canWrite = useHasRole('owner');
  const [tab, setTab] = useState<Tab>('products');
  const [setupOpen, setSetupOpen] = useState(false);

  const { data: categories = [] } = useQuery({
    queryKey: ['inventory-categories'],
    queryFn: fetchCategories,
  });
  const { data: products = [] } = useQuery({
    queryKey: ['inventory'],
    queryFn: fetchAllInventory,
  });
  const { data: brands = [] } = useQuery({
    queryKey: ['inventory-brands'],
    queryFn: () => fetchBrands(),
  });

  return (
    <div>
      <PageHeader
        title="Inventory"
        subtitle="Manage products, stock, and suppliers"
        actions={
          <>
            {canWrite && (
              <Button variant="outline" onClick={() => setSetupOpen(true)}>
                <Settings2 className="h-4 w-4" />
                <span className="hidden sm:inline">Units & sub-brands</span>
              </Button>
            )}
            {canWrite && tab === 'products' && <ImportProductsDialog />}
          </>
        }
      />

      <div className="mb-4 flex flex-wrap items-center gap-3 rounded-lg border border-gold-200 bg-gold-50/60 p-3 text-sm">
        <span className="font-medium">1. Choose store</span>
        <StoreSwitcher />
        <span className="text-muted-foreground">2. Category → 3. Brand → 4. Create or view products</span>
      </div>

      <div className="mb-6 flex flex-wrap gap-1 rounded-xl border bg-muted/40 p-1.5">
        {TABS.map((t) => {
          const active = tab === t.key;
          const count =
            t.key === 'categories'
              ? categories.length
              : t.key === 'brands'
                ? brands.length
                : t.key === 'products'
                  ? products.length
                  : undefined;
          return (
            <button
              key={t.key}
              type="button"
              onClick={() => setTab(t.key)}
              className={cn(
                'flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-all',
                active
                  ? 'bg-gold-500 text-white shadow-sm'
                  : 'text-muted-foreground hover:bg-background hover:text-foreground',
              )}
            >
              <t.icon className="h-4 w-4" />
              {t.label}
              {count !== undefined && (
                <span className={cn('rounded-full px-1.5 text-[10px]', active ? 'bg-white/25' : 'bg-muted')}>
                  {count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {tab === 'categories' && <CategoriesTab />}
      {tab === 'brands' && <BrandsTab />}
      {tab === 'products' && <ProductsTab />}
      {tab === 'inventory' && <InventoryLedgerTab />}
      {tab === 'adjustout' && <AdjustOutTab />}
      {tab === 'suppliers' && <SuppliersTab />}
      {tab === 'archived' && <ArchivedTab />}

      <InventorySetupDialog open={setupOpen} onOpenChange={setSetupOpen} />
    </div>
  );
}
